import { Sparkles, Wand2, BookOpen, GraduationCap, Layers, Loader2 } from "lucide-react";

const DIFFICULTIES = [
  { v: "easy", label: "Easy" },
  { v: "medium", label: "Medium" },
  { v: "hard", label: "Hard" },
  { v: "mixed", label: "Mixed" },
];
const STANDARDS = ["JEE Advanced", "University", "AP", "General"];

/**
 * Right-column config panel: subject / chapter / difficulty / exam standard /
 * count + the primary Generate button. Fully controlled — parent owns state.
 */
export default function SettingsPanel({
  subject, onSubjectChange, chapter, onChapterChange,
  difficulty, onDifficultyChange, standard, onStandardChange,
  count, onCountChange, busy, disabled, disabledLabel,
}) {
  return (
    <div className="qs-card p-6">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 grid place-items-center">
          <Wand2 className="w-4 h-4" />
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Generation settings</h3>
          <p className="text-xs text-slate-500">Tune the output before generating.</p>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Subject
          </label>
          <input
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            placeholder="Physics"
            data-testid="ai-textbook-subject"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" /> Chapter title
          </label>
          <input
            required
            value={chapter}
            onChange={(e) => onChapterChange(e.target.value)}
            className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            placeholder="e.g. Kinematics — Projectile Motion"
            data-testid="ai-textbook-chapter"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Difficulty</label>
          <div className="grid grid-cols-4 gap-1.5 mt-1.5">
            {DIFFICULTIES.map((d) => (
              <button
                key={d.v}
                type="button"
                onClick={() => onDifficultyChange(d.v)}
                className={`px-2 py-2 rounded-xl border text-xs font-semibold transition ${
                  difficulty === d.v
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                }`}
                data-testid={`ai-textbook-difficulty-${d.v}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600 flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5" /> Exam standard
          </label>
          <select
            value={standard}
            onChange={(e) => onStandardChange(e.target.value)}
            className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
            data-testid="ai-textbook-standard"
          >
            {STANDARDS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">
            Number of questions <span className="text-slate-400 font-medium tracking-normal normal-case">({count})</span>
          </label>
          <input
            type="range"
            min={1}
            max={25}
            value={count}
            onChange={(e) => onCountChange(Number(e.target.value))}
            className="w-full mt-3 accent-indigo-600"
            data-testid="ai-textbook-count"
          />
        </div>

        <button
          type="submit"
          disabled={busy || disabled}
          className="btn-primary w-full justify-center disabled:opacity-70"
          data-testid="ai-textbook-generate"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy
            ? "Generating with Gemini…"
            : disabled
              ? (disabledLabel || "Unavailable")
              : `Generate ${count} question${count === 1 ? "" : "s"}`}
        </button>
        <p className="text-[11px] text-slate-500 text-center">
          Uses Gemini Flash multimodal · Files are analyzed then discarded.
        </p>
      </div>
    </div>
  );
}
